import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, Download, Truck, Loader2 } from "lucide-react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { generateDeliveryNotePdf, DeliveryNoteData } from "@/lib/deliveryNotePdf";
import { extractFiscalQrFromPdf } from "@/lib/extractFiscalQrFromPdf";
import { useBureaucracyDocuments } from "@/hooks/useBureaucracyDocuments";

const OPEN_EVENT = "hexatech:open-transport-guide";
export function openTransportGuideDialog(d: DeliveryNoteData) {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: d }));
}

const toLocal = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const toIso = (v: string) => (v ? new Date(v).toISOString() : null);

export function TransportGuideDialogHost() {
  const [data, setData] = useState<DeliveryNoteData | null>(null);
  const [issue, setIssue] = useState("");
  const [loadDt, setLoadDt] = useState("");
  const [unloadDt, setUnloadDt] = useState("");
  const [plate, setPlate] = useState("");
  const [carrier, setCarrier] = useState("");
  const [driver, setDriver] = useState("");
  const [volumes, setVolumes] = useState("");
  const [weight, setWeight] = useState("");
  const [qrPreview, setQrPreview] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const { documents: vehicleDocs } = useBureaucracyDocuments("vehicle");
  const vehicles = vehicleDocs
    .map((d: any) => ({ plate: String(d.metadata?.plate || "").trim(), name: d.related_entity_name || d.title }))
    .filter((v) => v.plate);

  useEffect(() => {
    const listener = (e: Event) => {
      const d = (e as CustomEvent<DeliveryNoteData>).detail;
      setData(d);
      setIssue(toLocal(d.issueDate));
      setLoadDt(toLocal(d.loadingDateTime));
      setUnloadDt(toLocal(d.unloadingDateTime));
      setPlate(d.carrier.vehiclePlate || "");
      setCarrier(d.carrier.name || "");
      setDriver(d.carrier.driverName || "");
      setVolumes(d.totalVolumes != null ? String(d.totalVolumes) : "");
      setWeight(d.totalWeightKg != null ? String(d.totalWeightKg) : "");
      setQrPreview(null);
      // Guia já emitida mas sem dados fiscais guardados: sincroniza em silêncio com o TOConline
      if (d.officialGuideId && d.orderId && (!d.atAtcud || !d.atQrCode || !d.atDocumentNumber)) {
        setQrLoading(true);
        supabase.functions
          .invoke("toconline-create-transport-guide", { body: { orderId: d.orderId } })
          .then(async ({ data: res }) => {
            if (!res || res.error) return;
            let fiscalQr = res.qr_code || d.atQrCode || null;
            if (!fiscalQr && d.officialGuideId) {
              const { data: pdf } = await supabase.functions.invoke("toconline-download-pdf", {
                body: { documentId: d.officialGuideId, includeData: true },
              });
              if (pdf?.pdfBase64) fiscalQr = await extractFiscalQrFromPdf(pdf.pdfBase64);
              if (fiscalQr && d.orderId) {
                await supabase
                  .from("orders")
                  .update({ toconline_transport_guide_qr_code: fiscalQr })
                  .eq("id", d.orderId);
              }
            }
            setData((cur) =>
              cur && cur.orderId === d.orderId
                ? {
                    ...cur,
                    atDocumentNumber: res.at_code || cur.atDocumentNumber || null,
                    atAtcud: res.atcud || res.at_atcud || cur.atAtcud || null,
                    atQrCode: fiscalQr || cur.atQrCode || null,
                  }
                : cur
            );
          })
          .catch(() => { /* silencioso — o PDF sai na mesma sem esses campos */ })
          .finally(() => setQrLoading(false));
      }
    };
    window.addEventListener(OPEN_EVENT, listener);
    return () => window.removeEventListener(OPEN_EVENT, listener);
  }, []);

  useEffect(() => {
    let active = true;
    if (!data?.atQrCode) {
      setQrPreview(null);
      return () => { active = false; };
    }
    QRCode.toDataURL(data.atQrCode, { margin: 0, width: 256 })
      .then((url) => { if (active) setQrPreview(url); })
      .catch(() => { if (active) setQrPreview(null); });
    return () => { active = false; };
  }, [data?.atQrCode]);

  const build = async () => {
    if (!data) return null;
    const final: DeliveryNoteData = {
      ...data,
      issueDate: toIso(issue) || new Date().toISOString(),
      loadingDateTime: toIso(loadDt),
      unloadingDateTime: toIso(unloadDt),
      carrier: { name: carrier || null, vehiclePlate: plate ? plate.toUpperCase() : null, driverName: driver || null },
      totalVolumes: volumes ? Number(volumes) : null,
      totalWeightKg: weight ? Number(weight) : null,
    };
    return { final, doc: await generateDeliveryNotePdf(final) };
  };

  const handlePrint = async () => {
    try {
      const r = await build();
      if (!r) return;
      const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
        import.meta.url,
      ).toString();
      const pdf = await pdfjs.getDocument({ data: new Uint8Array(r.doc.output("arraybuffer")) }).promise;
      const pages: string[] = [];
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        const page = await pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const context = canvas.getContext("2d");
        if (!context) throw new Error("Não foi possível preparar a impressão");
        await page.render({ canvas, canvasContext: context, viewport }).promise;
        pages.push(canvas.toDataURL("image/png"));
      }
      const frame = document.createElement("iframe");
      frame.title = "Impressão da Guia de Transporte";
      frame.style.position = "fixed";
      frame.style.width = "1px";
      frame.style.height = "1px";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.opacity = "0";
      frame.srcdoc = `<!doctype html><html><head><title>${r.final.documentNumber}</title><style>@page{size:A4;margin:0}*{box-sizing:border-box}html,body{margin:0;padding:0;background:white}.page{display:block;width:210mm;height:297mm;object-fit:contain;page-break-after:always}.page:last-child{page-break-after:auto}</style></head><body>${pages.map((page) => `<img class="page" src="${page}" alt="">`).join("")}</body></html>`;
      frame.onload = () => {
        window.setTimeout(() => {
          frame.contentWindow?.focus();
          frame.contentWindow?.print();
        }, 300);
      };
      document.body.appendChild(frame);
      window.setTimeout(() => {
        frame.remove();
      }, 60_000);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível imprimir a guia");
    }
  };
  const handleSave = async () => {
    const r = await build();
    if (!r) return;
    r.doc.save(`${r.final.documentNumber}.pdf`);
    toast.success("Guia de transporte guardada");
    setData(null);
  };

  const [emitting, setEmitting] = useState(false);
  const handleEmitToconline = async () => {
    if (!data?.orderId || emitting) return;
    if (!window.confirm("Emitir Guia de Transporte oficial no TOConline? A guia é comunicada à AT e não pode ser apagada.")) return;
    setEmitting(true);
    try {
      const { data: res, error } = await supabase.functions.invoke("toconline-create-transport-guide", {
        body: { orderId: data.orderId, vehiclePlate: plate ? plate.toUpperCase() : undefined },
      });
      if (error) {
        let msg = error.message;
        try {
          const ctx = await (error as any).context?.json?.();
          if (ctx?.error) msg = ctx.error;
        } catch { /* ignore */ }
        throw new Error(msg);
      }
      if (res?.error) throw new Error(res.error);
      const parts = [`Guia ${res.guide_number}`];
      if (res.at_code) parts.push(`Código AT ${res.at_code}`);
      toast.success(res.already ? `Já emitida: ${parts.join(" · ")}` : `Emitida e comunicada à AT: ${parts.join(" · ")}`);
      setData({
        ...data,
        officialGuideId: res.guide_id ? String(res.guide_id) : data.officialGuideId || null,
        documentNumber: res.guide_number ? String(res.guide_number) : data.documentNumber,
        issueDate: res.guide_date || data.issueDate,
        atDocumentNumber: res.at_code || data.atDocumentNumber || null,
        atAtcud: res.atcud || res.at_atcud || data.atAtcud || null,
        atQrCode: res.qr_code || data.atQrCode || null,
      });
    } catch (e: any) {
      toast.error(e?.message || "Não foi possível emitir a guia no TOConline");
    } finally {
      setEmitting(false);
    }
  };

  const u = data?.unloading;
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && setData(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Guia de Transporte {data?.documentNumber}</DialogTitle>
          <DialogDescription className="sr-only">Consulte e ajuste os dados da guia antes de imprimir ou guardar.</DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3 rounded-md border p-3">
              <div>
                <div className="text-xs text-muted-foreground">Cliente</div>
                <div className="font-medium">{data.company.name}</div>
                {data.company.nif && <div className="text-xs">NIF {data.company.nif}</div>}
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Descarga</div>
                <div>{[u?.address, u?.postalCode, u?.city].filter(Boolean).join(", ") || "—"}</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div><Label>Data de emissão</Label><Input type="datetime-local" value={issue} onChange={(e) => setIssue(e.target.value)} /></div>
              <div><Label>Início (carga)</Label><Input type="datetime-local" value={loadDt} onChange={(e) => setLoadDt(e.target.value)} /></div>
              <div><Label>Descarga</Label><Input type="datetime-local" value={unloadDt} onChange={(e) => setUnloadDt(e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Matrícula</Label>
                <Input list="vehicle-plates" value={plate} placeholder="00-AA-00" onChange={(e) => setPlate(e.target.value)} />
                <datalist id="vehicle-plates">
                  {vehicles.map((v) => <option key={v.plate} value={v.plate}>{v.name}</option>)}
                </datalist>
              </div>
              <div><Label>Transportador</Label><Input value={carrier} onChange={(e) => setCarrier(e.target.value)} /></div>
              <div><Label>Motorista</Label><Input value={driver} onChange={(e) => setDriver(e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Volumes</Label><Input type="number" value={volumes} onChange={(e) => setVolumes(e.target.value)} /></div>
              <div><Label>Peso (kg)</Label><Input type="number" step="0.01" value={weight} onChange={(e) => setWeight(e.target.value)} /></div>
            </div>

            {(data.atDocumentNumber || data.atAtcud || qrPreview) && (
              <div className="grid grid-cols-[1fr_1fr_auto] items-center gap-3 rounded-md border bg-muted/40 p-3">
                <div>
                  <div className="text-xs text-muted-foreground">Código AT</div>
                  <div className="font-medium">{data.atDocumentNumber || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">ATCUD</div>
                  <div className="font-medium">{data.atAtcud || "—"}</div>
                </div>
                {qrPreview && <img src={qrPreview} alt="Código QR fiscal da Guia de Transporte" className="h-20 w-20" />}
              </div>
            )}

            {data.officialGuideId && (
              <div className="rounded-md border bg-muted/40 p-3">
                <div className="text-xs text-muted-foreground">Número da Guia no TOConline</div>
                <div className="font-semibold">{data.documentNumber}</div>
              </div>
            )}

            <div className="rounded-md border">
              <table className="w-full text-xs">
                <thead className="bg-muted"><tr><th className="p-2 text-left">Artigo</th><th className="p-2 text-left">Ref.</th><th className="p-2 text-right">Qtd.</th></tr></thead>
                <tbody>
                  {data.items.map((it, i) => (
                    <tr key={i} className="border-t"><td className="p-2">{it.productName}</td><td className="p-2">{it.sku || "—"}</td><td className="p-2 text-right">{it.quantity}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          <div>
            {data?.orderId && !data.officialGuideId && (
              <Button variant="secondary" onClick={handleEmitToconline} disabled={emitting}>
                {emitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Truck className="h-4 w-4 mr-2" />}
                Emitir no TOConline
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint} disabled={qrLoading}>
              {qrLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}Imprimir
            </Button>
            <Button onClick={handleSave} disabled={qrLoading}>
              {qrLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Guardar PDF
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
